import { useMemo } from "react"
import { DELETE_ACCOUNT_CONFIRMATION, ACCOUNT_STATUS_CONFIRMATION, CANCEL_DELETION } from "../constants"
import PropTypes from "prop-types"
import { useSelector } from "react-redux"
import moment from "moment"
import { Trans } from "react-i18next"

export const useConfirmModalContext = ({ isDeactivated, isOpen, propsFor, t, scheduledDeletionDate, isScheduleDeletion }) => {
  const isDeleteConfirmOpen = isOpen(DELETE_ACCOUNT_CONFIRMATION)
  const isStatusConfirmOpen = isOpen(ACCOUNT_STATUS_CONFIRMATION)
  const isCancelDeletionOpen = isOpen(CANCEL_DELETION)
  const showConfirmModal = isDeleteConfirmOpen || isStatusConfirmOpen || isCancelDeletionOpen
  const { last_booking_date } = useSelector((state) => state.user?.guide)
  const formattedLastBookingDate = last_booking_date ? moment(last_booking_date).format("MMMM D, YYYY") : ""

  const confirmCtx = useMemo(() => {
    if (isDeleteConfirmOpen) {
      return {
        title: t("settings.confirm_account_deletion"),
        text: (
          <Trans
            i18nKey={
              isScheduleDeletion ? "settings.account_deletion_scheduled_description" : "settings.confirm_account_deletion_description"
            }
            values={{ date: formattedLastBookingDate }}
          />
        ),
        primaryLabel: t("settings.confirm_deletion"),
        primaryColor: "danger"
      }
    }
    if (isCancelDeletionOpen) {
      return {
        title: t("settings.cancel_deletion_title"),
        text: t("settings.cancel_deletion_description", { scheduledDeletionDate }),
        primaryLabel: t("settings.cancel_deletion"),
        primaryColor: "primary"
      }
    }
    if (isDeactivated) {
      return {
        title: t("settings.confirm_activate"),
        text: t("settings.confirm_activate_account_description"),
        primaryLabel: t("settings.confirm_activate"),
        primaryColor: "primary"
      }
    }
    return {
      title: t("settings.confirm_account_deactivate"),
      text: t("settings.confirm_deactivate_account_description"),
      primaryLabel: t("settings.confirm_deactivate"),
      primaryColor: "danger"
    }
  }, [isDeleteConfirmOpen, isCancelDeletionOpen, isDeactivated, t, scheduledDeletionDate])

  const confirmProps = useMemo(() => {
    if (isDeleteConfirmOpen) return propsFor(DELETE_ACCOUNT_CONFIRMATION)
    if (isCancelDeletionOpen) return propsFor(CANCEL_DELETION)
    return propsFor(ACCOUNT_STATUS_CONFIRMATION)
  }, [isDeleteConfirmOpen, isCancelDeletionOpen, propsFor])

  return { isDeleteConfirmOpen, isStatusConfirmOpen, isCancelDeletionOpen, showConfirmModal, confirmCtx, confirmProps }
}

useConfirmModalContext.propTypes = {
  isDeactivated: PropTypes.bool,
  isOpen: PropTypes.func,
  propsFor: PropTypes.func,
  t: PropTypes.func,
  scheduledDeletionDate: PropTypes.string,
  isScheduleDeletion: PropTypes.bool
}
